import { Fragment } from 'react';

import path from 'path';

import { Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { Card, RouterLink, TwoLineGrid } from '~components';
import { pagePathSegments, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import { KeyGroup, KeyGroupService } from '~features/keyGroups';
import KeysService from '~features/keys/services/keysService';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { ResourceGroupService } from '~features/resourceGroups';
import useAuthorize from '~hooks/useAuthorize';
import { getService } from '~services/serviceFactory';

interface AssignmentCardCardProps {
	keyId: string;
	organisationId?: string | undefined;
}

const keysService = getService(KeysService);
const organisationsService = getService(OrganisationsService);
const keyGroupService = getService(KeyGroupService);
const resourceGroupService = getService(ResourceGroupService);

const AssignmentCard = ({ keyId, organisationId }: AssignmentCardCardProps) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { authorize } = useAuthorize();
	const isSuperAdmin = authorize([UserRoleEnum.SuperAdmin]);

	// Key data fetch
	const {
		data: keyData,
		error: keyError,
		isLoading: keyIsLoading,
	} = useSWR(
		keysService.getKeyByIdCacheKey(keyId, organisationId),
		([_, id, orgId]) => (id ? keysService.getKeyById(id, orgId ?? undefined) : null),
		{
			revalidateOnMount: true,
		}
	);

	const isKeyLoading = keyIsLoading && keyData == undefined;
	const hasKeyError = keyError != undefined;

	// Organisation data fetch
	const effectiveOrganisationId = organisationId ?? keyData?.organisationId?.toString();
	const organisationLink =
		effectiveOrganisationId ?
			`/${pagePathSegments.Organisations}/${effectiveOrganisationId}/${pagePathSegments.General}`
		:	undefined;
	const {
		data: organisationData,
		error: organisationError,
		isLoading: organisationIsLoading,
	} = useSWR(
		() => organisationsService.getOrganisationByIdCacheKey(effectiveOrganisationId),
		([_, id]) => (id ? organisationsService.getOrganisationById(id) : null),
		{
			revalidateOnMount: true,
		}
	);

	const isOrganisationLoading = organisationIsLoading && organisationData == undefined;
	const hasOrganisationError = organisationError != undefined;

	// Key group data fetch
	const {
		data: keyGroupData,
		error: keyGroupError,
		isLoading: keyGroupIsLoading,
	} = useSWR<KeyGroup[]>(
		keyGroupService.getKeyGroupsByKeyIdCacheKey(keyId, organisationId),
		([_basePath, _keyPath, keyId, organisationId]) => {
			return keyGroupService.getKeyGroupsByKeyId({
				keyId: keyId,
				overwrittenClientId: organisationId,
			});
		},
		{
			revalidateOnMount: true,
		}
	);

	const isKeyGroupLoading = keyGroupIsLoading && keyGroupData == undefined;
	const hasKeyGroupError = keyGroupError != undefined;

	// Resource group data fetch
	const resourceGroupId = keyData?.resourceGroupId?.toString();

	const {
		data: resourceGroupData,
		error: resourceGroupError,
		isLoading: resourceGroupIsLoading,
	} = useSWR(
		resourceGroupService.getResourceGroupByIdCacheKey(resourceGroupId, effectiveOrganisationId),
		([_, id, orgId]) =>
			id ? resourceGroupService.getResourceGroupById(id, orgId ?? undefined) : null,
		{
			revalidateOnMount: true,
		}
	);

	const isResourceGroupLoading = resourceGroupIsLoading && resourceGroupData == undefined;
	const hasResourceGroupError = resourceGroupError != undefined;

	// Field values
	const organisationName = organisationData?.name ?? t('unassigned', { ns: 'status' });
	const keyGroupList =
		keyGroupData && keyGroupData.length > 0 ?
			keyGroupData.map((keyGroup, index) => (
				<Fragment key={keyGroup.id}>
					<RouterLink
						to={{
							pathname: path.join('/', pagePathSegments.KeyGroups, keyGroup.id),
							search:
								organisationId ? `${queryParameters.OverwrittenClientId}=${organisationId}` : '',
						}}
					>
						{keyGroup.name}
					</RouterLink>
					{index == keyGroupData.length - 1 ? '.' : ', '}
				</Fragment>
			))
		:	<Typography variant='label' color={palette.textPrimary.main}>
				{t('none', { ns: 'status' })}
			</Typography>;
	const resourceGroupName = resourceGroupData?.name ?? t('none', { ns: 'status' });

	const hasError = hasKeyError || hasOrganisationError || hasKeyGroupError || hasResourceGroupError;
	const isInitialLoading =
		isKeyLoading || isOrganisationLoading || isKeyGroupLoading || isResourceGroupLoading;

	return (
		<Card title={t('assignment.labels.single', { ns: 'entities' })} sx={{ height: '100%' }}>
			<TwoLineGrid
				isLoading={isInitialLoading}
				showError={hasError}
				twoLineItems={[
					{
						primary: t('organisation.labels.single', { ns: 'entities' }),
						secondary: organisationName,
						link: organisationLink,
						linkTarget: organisationLink ? '_self' : undefined,
						isHidden: !isSuperAdmin,
					},
					{
						primary: t('resourceGroup.labels.single', { ns: 'entities' }),
						secondary: resourceGroupName,
					},
					{
						primary: t('keyGroup.labels.plural', { ns: 'entities' }),
						secondary: keyGroupList,
					},
				]}
			/>
		</Card>
	);
};

export default AssignmentCard;
